import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  CommunityGroup,
  GroupPost,
  GroupMember,
  PostListResponse,
  MemberListResponse,
  CreatePostRequest,
  UpdateGroupRequest,
} from "@/api/communityGroups/types";
import * as api from "@/api/communityGroups/communityGroupsApi";

interface GroupDetailState {
  group: CommunityGroup | null;
  posts: GroupPost[];
  members: GroupMember[];
  postsTotal: number;
  postsPage: number;
  postsLoading: boolean;
  membersTotal: number;
  membersPage: number;
  membersLoading: boolean;
  loading: boolean;
  error: string | null;
  activeTab: "posts" | "members" | "about";
}

const initialState: GroupDetailState = {
  group: null,
  posts: [],
  members: [],
  postsTotal: 0,
  postsPage: 1,
  postsLoading: false,
  membersTotal: 0,
  membersPage: 1,
  membersLoading: false,
  loading: false,
  error: null,
  activeTab: "posts",
};

export const fetchGroup = createAsyncThunk(
  "groupDetail/fetchGroup",
  async (uuid: string, { rejectWithValue }) => {
    try {
      const response = await api.fetchGroup(uuid);
      return response;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch group";
      return rejectWithValue(errorMessage);
    }
  },
);

export const updateGroup = createAsyncThunk(
  "groupDetail/updateGroup",
  async (
    { uuid, data }: { uuid: string; data: UpdateGroupRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.updateGroup(uuid, data);
      return response;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to update group";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchGroupPosts = createAsyncThunk(
  "groupDetail/fetchGroupPosts",
  async (
    { groupUuid, page }: { groupUuid: string; page?: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.fetchGroupPosts(groupUuid, { page });
      return response;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch posts";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchGroupMembers = createAsyncThunk(
  "groupDetail/fetchGroupMembers",
  async (
    { groupUuid, page }: { groupUuid: string; page?: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.fetchGroupMembers(groupUuid, { page });
      return response;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch members";
      return rejectWithValue(errorMessage);
    }
  },
);

export const createPost = createAsyncThunk(
  "groupDetail/createPost",
  async (
    { groupUuid, data }: { groupUuid: string; data: CreatePostRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.createPost(groupUuid, data);
      return response;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create post";
      return rejectWithValue(errorMessage);
    }
  },
);

export const deletePost = createAsyncThunk(
  "groupDetail/deletePost",
  async (postUuid: string, { rejectWithValue }) => {
    try {
      await api.deletePost(postUuid);
      return postUuid;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to delete post";
      return rejectWithValue(errorMessage);
    }
  },
);

export const reactToPost = createAsyncThunk(
  "groupDetail/reactToPost",
  async (
    {
      postUuid,
      reactionType,
    }: {
      postUuid: string;
      reactionType:
        | "like"
        | "love"
        | "celebrate"
        | "support"
        | "insightful"
        | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.reactToPost(postUuid, { reactionType });
      return response;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to react to post";
      return rejectWithValue(errorMessage);
    }
  },
);

export const joinGroup = createAsyncThunk(
  "groupDetail/joinGroup",
  async (groupUuid: string, { rejectWithValue }) => {
    try {
      const response = await api.joinGroup(groupUuid);
      return response;
    } catch (error: unknown) {
      // Extract the actual backend error message from Axios error
      const axiosError = error as {
        response?: { data?: { message?: string; error?: string } };
        message?: string;
      };
      const errorMessage =
        axiosError?.response?.data?.message ||
        axiosError?.response?.data?.error ||
        axiosError?.message ||
        "Failed to join group";
      return rejectWithValue(errorMessage);
    }
  },
);

export const leaveGroup = createAsyncThunk(
  "groupDetail/leaveGroup",
  async (groupUuid: string, { rejectWithValue }) => {
    try {
      await api.leaveGroup(groupUuid);
      return groupUuid;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to leave group";
      return rejectWithValue(errorMessage);
    }
  },
);

export const deleteGroup = createAsyncThunk(
  "groupDetail/deleteGroup",
  async (uuid: string, { rejectWithValue }) => {
    try {
      await api.deleteGroup(uuid);
      return uuid;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to delete group";
      return rejectWithValue(errorMessage);
    }
  },
);

export const updateMemberRole = createAsyncThunk(
  "groupDetail/updateMemberRole",
  async (
    {
      groupUuid,
      employeeUuid,
      role,
    }: {
      groupUuid: string;
      employeeUuid: string;
      role: "admin" | "moderator" | "member";
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.updateMemberRole(groupUuid, employeeUuid, {
        role,
      });
      return response;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to update member role";
      return rejectWithValue(errorMessage);
    }
  },
);

export const removeMember = createAsyncThunk(
  "groupDetail/removeMember",
  async (
    { groupUuid, employeeUuid }: { groupUuid: string; employeeUuid: string },
    { rejectWithValue },
  ) => {
    try {
      await api.removeMember(groupUuid, employeeUuid);
      return employeeUuid;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to remove member";
      return rejectWithValue(errorMessage);
    }
  },
);

const groupDetailSlice = createSlice({
  name: "groupDetail",
  initialState,
  reducers: {
    setActiveTab: (
      state,
      action: PayloadAction<"posts" | "members" | "about">,
    ) => {
      state.activeTab = action.payload;
    },
    clearGroup: (state) => {
      state.group = null;
      state.posts = [];
      state.members = [];
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch group
      .addCase(fetchGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchGroup.fulfilled,
        (state, action: PayloadAction<CommunityGroup>) => {
          state.loading = false;
          state.group = action.payload;
          // Backend may not set isMember field, so derive it from membershipStatus/membershipRole
          if (state.group && state.group.isMember === undefined) {
            state.group.isMember =
              state.group.membershipStatus === "active" ||
              state.group.membershipRole !== null;
          }
        },
      )
      .addCase(fetchGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update group
      .addCase(
        updateGroup.fulfilled,
        (state, action: PayloadAction<CommunityGroup>) => {
          state.group = action.payload;
        },
      )
      // Fetch posts
      .addCase(fetchGroupPosts.pending, (state) => {
        state.postsLoading = true;
      })
      .addCase(
        fetchGroupPosts.fulfilled,
        (state, action: PayloadAction<PostListResponse>) => {
          state.postsLoading = false;
          state.posts = action.payload.posts;
          state.postsTotal = action.payload.total;
          state.postsPage = action.payload.page;
        },
      )
      .addCase(fetchGroupPosts.rejected, (state) => {
        state.postsLoading = false;
      })
      // Fetch members
      .addCase(fetchGroupMembers.pending, (state) => {
        state.membersLoading = true;
      })
      .addCase(
        fetchGroupMembers.fulfilled,
        (state, action: PayloadAction<MemberListResponse>) => {
          state.membersLoading = false;
          state.members = action.payload.members;
          state.membersTotal = action.payload.total;
          state.membersPage = action.payload.page;
        },
      )
      .addCase(fetchGroupMembers.rejected, (state) => {
        state.membersLoading = false;
      })
      // Create post
      .addCase(
        createPost.fulfilled,
        (state, action: PayloadAction<GroupPost>) => {
          state.posts = [action.payload, ...state.posts];
          state.postsTotal += 1;
          if (state.group) {
            state.group.postCount += 1;
          }
        },
      )
      // Delete post
      .addCase(deletePost.fulfilled, (state, action: PayloadAction<string>) => {
        state.posts = state.posts.filter(
          (post) => post.uuid !== action.payload,
        );
        state.postsTotal -= 1;
        if (state.group) {
          state.group.postCount -= 1;
        }
      })
      // React to post
      .addCase(
        reactToPost.fulfilled,
        (state, action: PayloadAction<GroupPost>) => {
          const index = state.posts.findIndex(
            (p) => p.uuid === action.payload.uuid,
          );
          if (index !== -1) {
            state.posts[index] = action.payload;
          }
        },
      )
      // Join group
      .addCase(joinGroup.fulfilled, (state) => {
        if (state.group) {
          state.group.isMember = true;
          state.group.membershipStatus = "active";
          state.group.memberCount += 1;
        }
      })
      .addCase(joinGroup.rejected, (state, action) => {
        // If the error is "already a member", update state to reflect membership
        const errorMessage = (action.payload as string)?.toLowerCase() || "";
        const isAlreadyMember =
          errorMessage.includes("already a member") ||
          errorMessage.includes("already member") ||
          errorMessage.includes("already joined");
        if (isAlreadyMember && state.group) {
          state.group.isMember = true;
          state.group.membershipStatus = "active";
        }
      })
      // Leave group
      .addCase(leaveGroup.fulfilled, (state) => {
        if (state.group) {
          state.group.isMember = false;
          state.group.membershipStatus = null;
          state.group.membershipRole = null;
          state.group.memberCount -= 1;
        }
      })
      // Update member role
      .addCase(
        updateMemberRole.fulfilled,
        (state, action: PayloadAction<GroupMember>) => {
          const index = state.members.findIndex(
            (m) => m.uuid === action.payload.uuid,
          );
          if (index !== -1) {
            state.members[index] = action.payload;
          }
        },
      )
      // Remove member
      .addCase(
        removeMember.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.members = state.members.filter(
            (m) => m.employeeUuid !== action.payload,
          );
          state.membersTotal -= 1;
          if (state.group) {
            state.group.memberCount -= 1;
          }
        },
      );
  },
});

export const { setActiveTab, clearGroup, clearError } =
  groupDetailSlice.actions;
export default groupDetailSlice.reducer;
